import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import api from "@/fileshare/lib/api";
import { useAuth } from "@/fileshare/context/AuthContext";
import { formatApiErrorDetail } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { email, password, display_name: displayName });
      login(data.user);
      toast.success("Account created!");
      navigate("/fileshares/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap className="max-w-md mx-auto px-6 pt-20 pb-24">
      <div className="flex flex-col items-start mb-8">
        <div className="h-10 w-10 rounded-md bg-blue-500 flex items-center justify-center mb-5">
          <Share2 className="h-5 w-5 text-white" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Create your account</h1>
        <p className="text-sm text-slate-500 mt-1">Get a dashboard, bigger uploads & more.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div>
          <Label className="text-sm text-slate-700">Display name</Label>
          <Input data-testid="signup-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 bg-slate-50/60 border-slate-200" />
        </div>
        <div>
          <Label className="text-sm text-slate-700">Email</Label>
          <Input data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 bg-slate-50/60 border-slate-200" />
        </div>
        <div>
          <Label className="text-sm text-slate-700">Password</Label>
          <Input data-testid="signup-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 bg-slate-50/60 border-slate-200" />
        </div>
        {error && <p data-testid="signup-error" className="text-sm text-red-500">{error}</p>}
        <motion.div whileTap={{ scale: 0.99 }}>
          <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </motion.div>
      </form>

      <p className="text-sm text-slate-500 mt-6">
        Already have an account?{" "}
        <Link to="/fileshares/login" className="text-blue-500 font-medium hover:underline" data-testid="goto-login">Log in</Link>
      </p>
    </PageWrap>
  );
}
